import { useEffect, useMemo, useState } from "react";
import { api } from "../../App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Trophy, ArrowLeft } from "lucide-react";
import { MoneyCell } from "./money";

const fmt = (v) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v || 0);
const KEYS = [["awarded", "Awarded", "text-green-700"], ["bids", "Bid", "text-blue-700"], ["lost", "Lost", "text-red-700"]];

const NO_CALLS = { total: 0, success: 0, follow_up: 0, no_answer: 0 };

const buildTeam = (metrics, calls) => {
  const map = {};
  const blank = (name) => ({ name, rows: [], calls: NO_CALLS, awarded: { count: 0, value: 0 }, bids: { count: 0, value: 0 }, lost: { count: 0, value: 0 } });
  (calls || []).forEach((c) => { (map[c.name] ||= blank(c.name)).calls = c; });
  KEYS.forEach(([key]) => {
    (metrics?.[key]?.items || []).forEach((r) => {
      const name = r.sales_person_name || "-";
      const p = (map[name] ||= blank(name));
      p[key].count += 1;
      p[key].value += r.value || 0;
      p.rows.push({ ...r, bucket: key });
    });
  });
  return Object.values(map)
    .map((p) => {
      const decided = p.awarded.count + p.lost.count;
      return { ...p, winRate: decided ? Math.round((p.awarded.count / decided) * 100) : null };
    })
    .sort((a, b) => b.awarded.value - a.awarded.value || b.bids.value - a.bids.value || b.calls.total - a.calls.total);
};

const CountValue = ({ c, tone }) => (
  <TableCell className="whitespace-nowrap">
    <span className={`font-semibold ${tone}`}>{fmt(c.value)}</span>
    <span className="text-xs text-gray-500 ml-1">({c.count})</span>
  </TableCell>
);

const PersonRows = ({ person }) => (
  <Table>
    <TableHeader>
      <TableRow>
        <TableHead>Status</TableHead>
        <TableHead>Estimation #</TableHead>
        <TableHead>Client</TableHead>
        <TableHead>Project</TableHead>
        <TableHead>Date</TableHead>
        <TableHead className="text-right">Value</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {person.rows.map((r, i) => {
        const [, label, tone] = KEYS.find(([k]) => k === r.bucket);
        return (
          <TableRow key={`${r.id}-${r.bucket}-${i}`} data-testid={`team-perf-est-row-${r.estimation_number}-${r.bucket}`}>
            <TableCell className={`font-semibold text-xs uppercase ${tone}`}>{label}{r.is_partial ? " (part)" : ""}</TableCell>
            <TableCell className="whitespace-nowrap">{r.estimation_number}<div className="text-xs text-gray-500">v{r.version}</div></TableCell>
            <TableCell>{r.client_name}</TableCell>
            <TableCell>{r.project_name}</TableCell>
            <TableCell className="whitespace-nowrap">{(r.bucket === "awarded" ? r.awarded_date : r.submitted_date) || "-"}</TableCell>
            <TableCell className="text-right"><MoneyCell usd={r.value} native={r.native_value} currency={r.currency} /></TableCell>
          </TableRow>
        );
      })}
    </TableBody>
  </Table>
);

const TeamTable = ({ team, onPick }) => (
  <Table>
    <TableHeader>
      <TableRow>
        <TableHead>Salesperson</TableHead>
        <TableHead>Active Bids</TableHead>
        <TableHead>Awarded</TableHead>
        <TableHead>Lost</TableHead>
        <TableHead className="text-right">Win Rate</TableHead>
        <TableHead className="text-right">Calls</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {team.map((p) => (
        <TableRow key={p.name} className="cursor-pointer hover:bg-gray-50" onClick={() => onPick(p)} data-testid={`team-perf-row-${p.name}`}>
          <TableCell className="font-medium text-blue-700">{p.name}</TableCell>
          <CountValue c={p.bids} tone="text-blue-800" />
          <CountValue c={p.awarded} tone="text-green-800" />
          <CountValue c={p.lost} tone="text-red-800" />
          <TableCell className="text-right font-semibold" data-testid={`team-perf-winrate-${p.name}`}>{p.winRate === null ? "-" : `${p.winRate}%`}</TableCell>
          <TableCell className="text-right whitespace-nowrap" data-testid={`team-perf-calls-${p.name}`}>
            <div className="font-semibold">{p.calls.total}</div>
            {p.calls.total > 0 && (
              <div className="text-[11px] text-gray-500">
                <span className="text-green-700">{p.calls.success} ok</span> · <span className="text-amber-700">{p.calls.follow_up} f/u</span> · <span>{p.calls.no_answer} n/a</span>
              </div>
            )}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

export const TeamPerformanceCard = ({ metrics, periodLabel, range }) => {
  const [open, setOpen] = useState(false);
  const [person, setPerson] = useState(null);
  const [calls, setCalls] = useState([]);
  useEffect(() => {
    api.get(`/call-logs/stats?start_date=${range.start}&end_date=${range.end}`).then((r) => setCalls(r.data)).catch(() => setCalls([]));
  }, [range.start, range.end]);
  const team = useMemo(() => buildTeam(metrics, calls), [metrics, calls]);
  const top = team[0]?.awarded.value > 0 ? team[0] : null;
  const close = () => { setOpen(false); setPerson(null); };
  return (
    <>
      <Card role="button" onClick={() => setOpen(true)} data-testid="sales-card-team" className="p-6 bg-violet-50 border-0 shadow-md3-1 hover:shadow-md3-2 hover:-translate-y-0.5 cursor-pointer transition-[box-shadow,transform] duration-200 rounded-md3-lg">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-md3-title-small text-violet-900 font-medium">Team Performance</h3>
          <div className="bg-violet-500/20 p-2.5 rounded-md3-md"><Trophy className="h-5 w-5 text-violet-700" /></div>
        </div>
        {top ? (
          <>
            <div className="text-md3-body-small text-violet-800">Top performer</div>
            <div className="text-md3-title-large font-bold text-violet-950 truncate" data-testid="sales-card-team-top-name">{top.name}</div>
            <p className="text-md3-body-small text-violet-800 mt-1" data-testid="sales-card-team-top-value">{fmt(top.awarded.value)} awarded · {top.awarded.count} project{top.awarded.count === 1 ? "" : "s"}</p>
          </>
        ) : (
          <p className="text-md3-body-medium text-violet-800 mt-2" data-testid="sales-card-team-empty">No awards in this period</p>
        )}
        <p className="text-xs text-violet-700 mt-2">{team.length} {team.length === 1 ? "salesperson" : "salespeople"} with activity</p>
      </Card>
      <Dialog open={open} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-5xl bg-white max-h-[85vh] overflow-y-auto" data-testid="team-perf-dialog">
          <DialogHeader>
            <DialogTitle className="text-md3-headline-small flex items-center gap-2">
              {person && (
                <Button size="sm" variant="ghost" onClick={() => setPerson(null)} data-testid="team-perf-back-btn"><ArrowLeft className="h-4 w-4" /></Button>
              )}
              {person ? person.name : "Team Performance"} <span className="text-md3-body-medium text-gray-500 font-normal">· {periodLabel}</span>
            </DialogTitle>
          </DialogHeader>
          {team.length === 0 ? (
            <p className="text-center py-8 text-gray-500" data-testid="team-perf-empty">No estimations in this period.</p>
          ) : person ? <PersonRows person={person} /> : <TeamTable team={team} onPick={setPerson} />}
          {!person && team.length > 0 && <p className="text-xs text-gray-500">Win rate = awarded ÷ (awarded + lost). Calls = calls logged by that person (ok = Successful, f/u = Follow-up Needed, n/a = No Answer). Click a salesperson to see their estimations.</p>}
        </DialogContent>
      </Dialog>
    </>
  );
};
